import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { Button } from "../components/ui/button";
import { Sparkles, HeartPulse, Utensils, Activity, LineChart, Leaf, Sun, Moon } from "lucide-react";

export default function Landing() {
  const { t, lang, setLang } = useLang();
  const { user } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <header className="glass-nav sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            <img src="/logo.png" alt="Gracely" className="w-8 h-8 object-contain" />
            <span className="font-serif-display text-2xl text-stone-900">Healthy Gracely</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-stone-600">
            <a href="#features" className="hover:text-[#4A5D23] transition-colors" data-testid="nav-features">{t("nav_features")}</a>
            <a href="#about" className="hover:text-[#4A5D23] transition-colors" data-testid="nav-about">{t("nav_about")}</a>
          </nav>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setLang(lang === "id" ? "en" : "id")}
              className="px-3 py-1.5 rounded-full text-xs font-semibold text-stone-700 bg-stone-100 hover:bg-stone-200 btn-pop"
              data-testid="language-toggle"
            >
              {lang === "id" ? "ID · EN" : "EN · ID"}
            </button>
            <button
              onClick={toggle}
              className="w-8 h-8 rounded-full text-stone-700 bg-stone-100 hover:bg-stone-200 btn-pop flex items-center justify-center"
              aria-label="Toggle theme"
              data-testid="theme-toggle-landing"
            >
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            {user ? (
              <Button onClick={() => nav("/dashboard")} className="rounded-full bg-[#4A5D23] hover:bg-[#3A4A1C] text-white btn-pop" data-testid="go-dashboard-btn">
                Dashboard
              </Button>
            ) : (
              <>
                <Link to="/login" className="text-sm text-stone-700 px-3 py-1.5 hover:text-[#4A5D23]" data-testid="nav-login">{t("nav_login")}</Link>
                <Button onClick={() => nav("/register")} className="rounded-full bg-[#E07A5F] hover:bg-[#C96D55] text-white btn-pop" data-testid="nav-register">
                  {t("nav_register")}
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 py-20 md:py-28 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7 fade-in">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#4A5D23] mb-6">{t("hero_kicker")}</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-stone-900">
            {t("hero_title")}
          </h1>
          <p className="mt-6 text-lg text-stone-600 leading-relaxed max-w-xl">{t("hero_sub")}</p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Button
              onClick={() => nav(user ? "/dashboard" : "/register")}
              className="rounded-full bg-[#E07A5F] hover:bg-[#C96D55] text-white px-8 py-6 text-base btn-pop"
              data-testid="hero-cta-primary"
            >
              {t("hero_cta_primary")}
            </Button>
            <a href="#features" className="text-stone-800 underline underline-offset-8 decoration-stone-300 hover:decoration-[#4A5D23]" data-testid="hero-cta-secondary">
              {t("hero_cta_secondary")}
            </a>
          </div>
          <div className="mt-14 flex items-center gap-6 text-xs text-stone-500">
            <div className="flex items-center gap-2"><HeartPulse className="w-4 h-4 text-[#4A5D23]" /> Empathetic AI</div>
            <div className="flex items-center gap-2"><Leaf className="w-4 h-4 text-[#4A5D23]" /> Evidence-based</div>
            <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#4A5D23]" /> Bilingual (ID · EN)</div>
          </div>
        </div>
        <div className="md:col-span-5 relative">
          <div className="rounded-3xl overflow-hidden shadow-[0_30px_80px_-30px_rgba(74,93,35,0.5)] rotate-[-2deg]">
            <img
              src="https://images.unsplash.com/photo-1461468611824-46457c0e11fd?crop=entropy&cs=srgb&fm=jpg&w=940&q=85"
              alt="Calm nature"
              className="w-full h-[460px] object-cover"
            />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-2xl shadow-lg border border-stone-100 max-w-[240px]">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2 h-2 rounded-full bg-[#81B29A]" />
              <span className="text-xs font-bold uppercase tracking-widest text-stone-500">Gracely</span>
            </div>
            <p className="text-sm text-stone-700 leading-snug">{`"Kamu berjalan 6.500 langkah kemarin — konsistensi luar biasa. Yuk lanjut hari ini."`}</p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-2xl mb-12">
          <h2 className="font-serif-display text-3xl sm:text-4xl text-stone-900 tracking-tight">{t("features_title")}</h2>
        </div>
        <div className="grid md:grid-cols-6 gap-6">
          <Feature icon={<HeartPulse className="w-6 h-6" />} title={t("f1_title")} desc={t("f1_desc")} span="md:col-span-3" testId="feature-anamnesis" />
          <Feature icon={<Activity className="w-6 h-6" />} title={t("f2_title")} desc={t("f2_desc")} span="md:col-span-3" testId="feature-workouts" image="https://images.unsplash.com/photo-1552196527-bffef41ef674?crop=entropy&cs=srgb&fm=jpg&w=940&q=85" />
          <Feature icon={<Utensils className="w-6 h-6" />} title={t("f3_title")} desc={t("f3_desc")} span="md:col-span-4" testId="feature-scan" image="https://images.pexels.com/photos/1192031/pexels-photo-1192031.jpeg?auto=compress&cs=tinysrgb&w=940" />
          <Feature icon={<Sparkles className="w-6 h-6" />} title={t("f4_title")} desc={t("f4_desc")} span="md:col-span-2" testId="feature-chat" />
          <Feature icon={<LineChart className="w-6 h-6" />} title={t("f5_title")} desc={t("f5_desc")} span="md:col-span-6" testId="feature-habits" />
        </div>
      </section>

      <section id="about" className="max-w-4xl mx-auto px-6 py-24 text-center">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#4A5D23] mb-6">Gracely</p>
        <h2 className="font-serif-display text-3xl sm:text-4xl text-stone-900 tracking-tight leading-tight">
          {lang === "id"
            ? "Bukan mendiagnosis. Mendampingi."
            : "Not a diagnosis. A companion."}
        </h2>
        <p className="mt-6 text-stone-600 leading-relaxed">
          {lang === "id"
            ? "Setiap saran Gracely bersandar pada literatur medis terverifikasi seperti WHO, CDC, dan jurnal peer-review. Tetap konsultasikan kondisi seriusmu dengan dokter."
            : "Every Gracely suggestion is grounded in verified medical literature from WHO, CDC, and peer-reviewed journals. Please still consult your doctor for serious conditions."}
        </p>
      </section>

      <footer className="border-t border-stone-200 py-10 text-center text-sm text-stone-500">
        {t("footer_note")}
      </footer>
    </div>
  );
}

function Feature({ icon, title, desc, span, image, testId }) {
  return (
    <div className={`card-soft p-8 ${span}`} data-testid={testId}>
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-[#EAF0E4] text-[#4A5D23] flex items-center justify-center">{icon}</div>
        <div className="flex-1">
          <h3 className="font-serif-display text-2xl text-stone-900 leading-tight">{title}</h3>
          <p className="mt-3 text-stone-600 leading-relaxed">{desc}</p>
        </div>
      </div>
      {image && (
        <div className="mt-6 rounded-xl overflow-hidden">
          <img src={image} alt="" className="w-full h-52 object-cover" />
        </div>
      )}
    </div>
  );
}
